import { useEffect, useState } from "react";
import { Button, Form, Modal, Spinner, Table } from "react-bootstrap";
import { BsWechat } from "react-icons/bs";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import signerApi from "../../../api/signerapi";

const Signers = ({ documentId, documentName }) => {
  const [signers, setSigners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [newSigner, setNewSigner] = useState({
    name: "",
    email: "",
  });

  useEffect(() => {
    if (documentId) {
      signerApi
        .getDetailsOfSignersById(documentId)
        .then((response) => {
          setSigners(response.data || []);
          setLoading(false);
        })
        .catch((err) => {
          setError("Failed to fetch signer details.");
          console.error("Error fetching signers:", err);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [documentId]);

  const handleaddSignerClick = () => {
    setShowModal(true);
  };
  const handleCloseModal = () => {
    setShowModal(false);
    setNewSigner({ name: "", email: "" });
  };
  const handleSaveAddSigner = async () => {
    const email = newSigner.email.trim().toLowerCase();

    if (!newSigner.name.trim()) {
      Swal.fire({
        icon: "error",
        title: "Name Required",
        text: "Please enter the signer's name.",
      });
      return;
    }

    if (!email) {
      Swal.fire({
        icon: "error",
        title: "Email Required",
        text: "Please enter the signer's email.",
      });
      return;
    }

    const isDuplicate = signers.some(
      (signer) => signer.email?.trim().toLowerCase() === email,
    );

    if (isDuplicate) {
      Swal.fire({
        icon: "error",
        title: "Duplicate Signer",
        text: "This signer has already been added.",
      });
      return;
    }

    try {
      const payload = {
        name: newSigner.name.trim(),
        email,
      };

      const response = await signerApi.AddSigner(documentId, payload);

      const savedSigner = response.data;

      setSigners((prev) => [
        ...prev,
        {
          id: savedSigner.id,
          name: savedSigner.name,
          email: savedSigner.email,
          signStatus: savedSigner.signStatus || "Not signed",
          signedAt: savedSigner.signedAt || "N/A",
        },
      ]);

      Swal.fire({
        title: "Success!",
        text: `Signer added successfully for Document ${documentName}!`,
        icon: "success",
        timer: 1500,
        showConfirmButton: false,
      });

      handleCloseModal();
    } catch (err) {
      console.error("Error adding signer:", err);

      Swal.fire({
        title: "Error",
        text: "Failed to add signer",
        icon: "error",
      });
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === "email") {
      const email = value.trim().toLowerCase();

      const isDuplicate = signers.some(
        (signer) => signer.email?.trim().toLowerCase() === email,
      );

      if (isDuplicate) {
        Swal.fire({
          icon: "error",
          title: "Duplicate Signer",
          text: "This signer has already been added.",
        });

        setNewSigner((prev) => ({
          ...prev,
          email: "",
        }));
        return;
      }
    }

    setNewSigner((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center">
        <Spinner animation="border" />
      </div>
    );
  }

  if (error) {
    return <div className="text-center text-danger">{error}</div>;
  }

  const handleChatClick = (signer) => {
    navigate("/dashboard/chat-app-signers", {
      state: {
        documentId,
        documentName,
        chatType: "document",
        signerEmail: signer.email,
        activeSidebar: "/dashboard/my-consents",
      },
    });
  };

  return (
    <div>
      <br />
      <div className="d-flex justify-content-between align-items-center">
        <h4>
          <strong>Details of Signers</strong>
        </h4>
      </div>
      <p>
        If you want to send any signer you can add here for this document and
        send {documentName}{" "}
        <Button variant="info" onClick={handleaddSignerClick}>
          Add Signer
        </Button>
      </p>
      <Table striped bordered hover className="mt-4">
        <thead>
          <tr>
            <th>ID</th>
            <th>Name</th>
            <th>Email</th>
            <th>Chat</th>
          </tr>
        </thead>
        <tbody>
          {signers.length === 0 ? (
            <tr>
              <td colSpan="4" className="text-center">
                No signers found
              </td>
            </tr>
          ) : (
            signers.map((signer, index) => (
              <tr key={signer.id ?? index}>
                <td>{signer.id ?? index + 1}</td>
                <td>{signer.name}</td>
                <td>{signer.email}</td>
                <td>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => handleChatClick(signer)}
                  >
                    <BsWechat />
                  </Button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </Table>
      <Modal show={showModal} onHide={handleCloseModal}>
        <Modal.Header closeButton>
          <Modal.Title>Add Signer</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group controlId="SignerName" className="mb-3">
              <Form.Label>Name</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter name"
                name="name"
                value={newSigner.name}
                onChange={handleInputChange}
              />
            </Form.Group>

            <Form.Group controlId="SignerEmail" className="mb-3">
              <Form.Label>Email</Form.Label>
              <Form.Control
                type="email"
                placeholder="Enter email"
                name="email"
                value={newSigner.email}
                onChange={handleInputChange}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSaveAddSigner}>
            Send Invite
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Signers;